import { useMemo } from "react";
import { Download, Trash2 } from "lucide-react";
import dayjs from "dayjs";

import { cn } from "@/utils/css";
import { BatchBadgeIcon } from "./BatchBadgeIcon";
import { BATCH_STATUS } from "./batchConstants";

const formatTimestamp = (date) => dayjs(date).format("MMM DD, HH:mm");

export const BatchPredictionRow = ({ prediction, onCancel, onDelete }) => {
    const { fileName, extension, status, createdAt, lineCount, downloadUrl, downloadName, warning, error } = prediction;
    const isProcessing = status === BATCH_STATUS.PROCESSING;
    const isFailed = status === BATCH_STATUS.FAILED;
    const isWarning = status === BATCH_STATUS.WARNING;

    const subline = useMemo(() => {
        if (isProcessing) return "Processing file...";
        if (isFailed) return null;
        if (typeof lineCount === "number") return `${lineCount} ${lineCount === 1 ? "line" : "lines"}`;
        return null;
    }, [isProcessing, isFailed, lineCount]);

    return (
        <div className="bg-cm-p-surface border border-cm-p-outline rounded-lg flex items-center gap-5 p-4">
            <div className="flex flex-1 min-w-0 items-center gap-3">
                <BatchBadgeIcon extension={extension} isProcessing={isProcessing} isFailed={isFailed} />
                <div className="flex flex-col justify-center min-w-0 flex-1">
                    <div className="flex items-center gap-3 min-w-0">
                        <p className="text-cm-p-text-primary text-sm font-medium truncate min-w-0">{fileName}</p>
                        {isWarning && warning ? (
                            <span className="bg-cm-p-surface-container text-cm-p-text-primary text-xs font-medium px-2 py-[2px] rounded-full whitespace-nowrap">
                                {warning}
                            </span>
                        ) : null}
                    </div>
                    {isFailed ? (
                        <p className="text-xs text-cm-p-on-container-red mt-0.5">
                            <span>Batch job failed.</span>
                            {error ? <span className="font-semibold"> {error}</span> : null}
                        </p>
                    ) : subline ? (
                        <p className="text-xs text-cm-p-text-secondary mt-0.5">{subline}</p>
                    ) : null}
                </div>
            </div>
            <p className="text-xs text-cm-p-text-secondary whitespace-nowrap">{formatTimestamp(createdAt)}</p>
            <div className="flex items-center gap-2 w-[170px] justify-end">
                {isProcessing ? (
                    <button
                        type="button"
                        onClick={() => onCancel(prediction)}
                        className="flex-1 h-10 px-4 rounded-md bg-cm-p-surface-hover text-cm-p-text-primary text-sm font-medium hover:bg-cm-p-surface-container-hover"
                    >
                        Cancel
                    </button>
                ) : (
                    <>
                        {downloadUrl ? (
                            <a
                                href={downloadUrl}
                                download={downloadName}
                                className="flex-1 h-10 rounded-md bg-cm-p-surface-hover hover:bg-cm-p-surface-container-hover text-cm-p-text-primary text-sm font-medium flex items-center justify-center gap-2 px-4"
                            >
                                <Download className="size-4" />
                                <span>Download</span>
                            </a>
                        ) : null}
                        <button
                            type="button"
                            onClick={() => onDelete(prediction)}
                            className={cn(
                                "h-10 rounded-md flex items-center justify-center",
                                isFailed
                                    ? "flex-1 bg-cm-p-surface-container hover:bg-cm-p-surface-container-hover text-cm-p-on-container-red"
                                    : "px-3 bg-cm-p-surface-hover hover:bg-cm-p-surface-container-hover text-cm-p-text-primary",
                            )}
                            aria-label="Delete"
                        >
                            <Trash2 className="size-4" />
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};
